/**
 * Formats an ISO date string into a localized date and time string
 * @param iso - The ISO date string to format
 * @param timezone - Optional IANA timezone (e.g. "America/Sao_Paulo"). Defaults to the browser's local zone.
 * @returns A formatted string in the format DD/MM/YY HH:mm
 */
export const formatDateTime = (iso?: string | null, timezone?: string): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const two = (n: number) => String(n).padStart(2, "0");
  if (timezone) {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date);
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
    return `${get("day")}/${get("month")}/${get("year").slice(-2)} ${get("hour")}:${get("minute")}`;
  }
  const dd = two(date.getDate());
  const mm = two(date.getMonth() + 1);
  const yy = String(date.getFullYear()).slice(-2);
  const hh = two(date.getHours());
  const min = two(date.getMinutes());
  return `${dd}/${mm}/${yy} ${hh}:${min}`;
};

/**
 * Returns YYYY-MM-DD for the current instant in the given IANA timezone.
 */
export const todayInTimezone = (timezone: string): string => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};
